import {
  Badge,
  BadgeColor,
  BadgeContainer,
} from '@delmonicos/shared-design-system-react/src/components/badge/badge';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Button,
  ButtonColor,
} from '@delmonicos/shared-design-system-react/src/components/button/button';
import {
  Dialog,
  DialogPosition,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  Icon,
  IconColor,
  IconSize,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  ResourceStatus,
  withProtectedResource,
} from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { GetLocationsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/locations/get-locations-paginated.model';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  OnChangeFn,
  SortingState,
  functionalUpdate,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { useEmspBoGetCposQuery } from '@app/core/service/emsp-bo-cpo.service';
import { useEmspBoGetLocationsQuery } from '@app/core/service/emsp-bo-location.service';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';

import { EmspBoLocationPullCreateController } from '../emsp-bo-location-pull-create/emsp-bo-location-pull-create.controller';
import {
  EmspBoEmspLocationListContextProvider,
  useEmspBoEmspLocationListContext,
} from '../emsp-bo-location.context';
import { EmspBoLocationListFilterSearchController } from './emsp-bo-location-list-filter-search/emsp-bo-location-list-filter-search.controller';
import { EmspBoLocationListFilterStateController } from './emsp-bo-location-list-filter-state/emsp-bo-location-list-filter-state.controller';
import { EmspBoLocationListFilterController } from './emsp-bo-location-list-filter/emsp-bo-location-list-filter.controller';
import {
  EMSP_BO_EMSP_LOCATION_LIST_SORT_FIELD,
  EMSP_BO_LOCATION_LIST_FILTER_NAME_LIST,
} from './emsp-bo-location-list.config';
import * as css from './emsp-bo-location-list.module.scss';
import { EmspBoLocationListView } from './emsp-bo-location-list.view';

export const EmspBoLocationListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const {
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    getPaginatedLimitList,
  } = useEmspBoEmspLocationListContext();

  // USER PERMISSIONS ///////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionFilterIsDialogOpen, setActionFilterIsDialogOpen] =
    useState(false);
  const [actionPullIsDialogOpen, setActionPullIsDialogOpen] = useState(false);

  // DATA FETCHING //////////////////////////////////////////////////////////////

  // Fetch the CPO list to auto-select the first one when no cpoId is set yet.
  // The API requires a valid UUID for cpoId — it rejects empty strings and absent values.
  const { data: cposData } = useEmspBoGetCposQuery({ limit: 100, offset: 0 });

  useEffect(() => {
    const firstCpoId = cposData?.docs.at(0)?.id;
    if (firstCpoId && getPaginatedQueryParameters.cpoId === '') {
      setGetPaginatedQueryParameters((current) => ({
        ...current,
        cpoId: firstCpoId,
      }));
    }
  }, [
    cposData,
    getPaginatedQueryParameters.cpoId,
    setGetPaginatedQueryParameters,
  ]);

  // Skip the locations query until a valid cpoId is resolved.
  const hasCpoId = getPaginatedQueryParameters.cpoId !== '';

  const sanitizedQueryParameters = useMemo(
    (): GetLocationsPaginatedModel => ({
      ...getPaginatedQueryParameters,
      // cpoId is always a valid UUID here (hasCpoId guard above ensures the skip)
      cpoId: getPaginatedQueryParameters.cpoId,
    }),
    [getPaginatedQueryParameters],
  );

  const queryState = useEmspBoGetLocationsQuery(sanitizedQueryParameters, {
    skip: !hasCpoId,
  });

  const paginationState = useMemo(
    () =>
      getState({
        // Treat skipped (uninitialized) state as loading while the first CPO is being fetched
        isLoading: queryState.isLoading || queryState.isUninitialized,
        isError: queryState.isError,
        isFetching: queryState.isFetching,
      }),
    [
      queryState.isLoading,
      queryState.isUninitialized,
      queryState.isError,
      queryState.isFetching,
    ],
  );

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.EMSP_BO,
        action: ActionsByResource.EMSP_BO.PULL_LOCATIONS,
      })
    ) {
      actions.push(
        <Button
          key="pull"
          color={ButtonColor.Primary}
          onClick={() => setActionPullIsDialogOpen(true)}>
          <Stack gap={GapEnum.S} alignItems="center">
            <Icon
              name="refresh-cw-01"
              color={IconColor.Inherit}
              iconSize={IconSize.Small}
            />
            {t('$.emsp-bo-location-pull-create.button')}
          </Stack>
        </Button>,
      );
    }

    setPageAction(actions);
  }, [setPageAction, userPermissions, t]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handlePaginationPageChange = (paginationPageNext: number): void => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      offset: (paginationPageNext - 1) * current.limit,
    }));
  };

  const handlePaginationLimitChange = (paginationLimitNext: number): void => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      limit: paginationLimitNext,
      offset: 0,
    }));
  };

  const handlePaginationSortingChange: OnChangeFn<SortingState> = (
    sortingState,
  ): void => {
    setGetPaginatedQueryParameters((current) => {
      const paginationSorting0 = functionalUpdate(sortingState, [
        {
          desc: current.sortOrder === -1,
          id: current.sortField ?? EMSP_BO_EMSP_LOCATION_LIST_SORT_FIELD,
        },
      ]).at(0);

      return {
        ...current,
        sortField:
          paginationSorting0?.id as GetLocationsPaginatedModel['sortField'],
        sortOrder: paginationSorting0?.desc ? -1 : 1,
      };
    });
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const filterActiveHiddenCount = Math.max(
    0,
    EMSP_BO_LOCATION_LIST_FILTER_NAME_LIST.filter((filterName) => {
      const value = getPaginatedQueryParameters[filterName];
      return value !== undefined && value !== '';
    }).length - 3,
  );

  const {
    Component: EmspBoEmspLocationListViewProtected,
    status: resourceStatus,
  } = withProtectedResource(EmspBoLocationListView, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.EMSP_BO,
        action: ActionsByResource.EMSP_BO.READ_LOCATION,
      }),
    transformDataToResource: transformPaginatedResponse,
  })({
    paginationLimit: getPaginatedQueryParameters.limit,
    paginationLimitList: getPaginatedLimitList,
    paginationState,
    paginationSorting: [
      {
        desc: getPaginatedQueryParameters.sortOrder === -1,
        id:
          getPaginatedQueryParameters.sortField ??
          EMSP_BO_EMSP_LOCATION_LIST_SORT_FIELD,
      },
    ],
    onPaginationLimitChange: handlePaginationLimitChange,
    onPaginationPageChange: handlePaginationPageChange,
    onPaginationSortingChange: handlePaginationSortingChange,
  });

  return (
    <Stack flex={1} className={css.listContainer} direction="column">
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Stack direction="row" justifyContent="space-between" gap={GapEnum.XL}>
          <Stack flex={1}>
            <EmspBoLocationListFilterSearchController
              onCancel={() => {}}
              onSuccess={() => {}}
            />
          </Stack>
          <EmspBoLocationListFilterStateController />
          <Stack justifyContent="end" gap={GapEnum.M}>
            <BadgeContainer
              badge={
                filterActiveHiddenCount > 0 && (
                  <Badge color={BadgeColor.Secondary}>
                    {`+${filterActiveHiddenCount}`}
                  </Badge>
                )
              }>
              <ButtonIcon
                icon="filter-funnel-01"
                onClick={() => setActionFilterIsDialogOpen(true)}
              />
            </BadgeContainer>
          </Stack>
        </Stack>
      )}
      <Stack className={css.tableContainer}>
        {EmspBoEmspLocationListViewProtected}
      </Stack>
      {resourceStatus === ResourceStatus.SUCCESS && (
        <>
          <Dialog
            open={actionFilterIsDialogOpen}
            onClose={() => setActionFilterIsDialogOpen(false)}
            position={DialogPosition.RIGHT}>
            <Dialog.Header
              icon="filter-funnel-01"
              onClose={() => setActionFilterIsDialogOpen(false)}
              title={t('$.emsp-bo-location-list-filter.title')}
            />
            <Dialog.Content>
              <EmspBoLocationListFilterController
                onCancel={() => setActionFilterIsDialogOpen(false)}
                onSuccess={() => setActionFilterIsDialogOpen(false)}
              />
            </Dialog.Content>
          </Dialog>
          <Dialog
            open={actionPullIsDialogOpen}
            onClose={() => setActionPullIsDialogOpen(false)}
            position={DialogPosition.RIGHT}>
            <Dialog.Header
              icon="refresh-cw-01"
              onClose={() => setActionPullIsDialogOpen(false)}
              title={t('$.emsp-bo-location-pull-create.title')}
            />
            <Dialog.Content>
              <EmspBoLocationPullCreateController
                cpoId={getPaginatedQueryParameters.cpoId}
                onCancel={() => setActionPullIsDialogOpen(false)}
                onSuccess={() => setActionPullIsDialogOpen(false)}
              />
            </Dialog.Content>
          </Dialog>
        </>
      )}
    </Stack>
  );
};

export const EmspBoEmspLocationListControllerWithContext =
  (): React.JSX.Element => {
    return (
      <EmspBoEmspLocationListContextProvider>
        <EmspBoLocationListController />
      </EmspBoEmspLocationListContextProvider>
    );
  };
